// ExportGiftVoucherUsed.ts
import ExcelJS from 'exceljs'
import { saveAs } from 'file-saver'
import moment from 'moment'
import { toBuddhistYear } from 'utils/app.utils'
import { forceTextOnDateCells } from 'utils/excel.utils'

export interface UsedRow {
    date: string   // "YYYY-MM-DD"
    receiptNumber: string
    cnNumber: string
    branchCnNumber: string
    snCode?: string
    patientName: string
    voucherType: string
    voucherName: string
    voucherCode: string
    voucherPrice: string
    voucherDiscount: number
}

export interface GroupedVoucher {
    voucherType: string
    vouchers: Array<{
        voucherName: string
        value: string
        countUsed: number
        sumDiscount: number
    }>
}

export interface GiftVoucherUsedReport {
    branchName: string
    dateStart?: string;
    dateEnd?: string;
    typeFilter: string   // e.g. "ทั้งหมด"
    giftFilter: string
    includeSn: boolean
    rows: UsedRow[]
    groups: GroupedVoucher[]
    t: (key: string) => string
}

export async function exportGiftVoucherUsedToExcel(rpt: GiftVoucherUsedReport) {
    const wb = new ExcelJS.Workbook()
    const ws: any = wb.addWorksheet(rpt.t('REPORT.TABLE.GIFT_VOUCHER_USED.TITLE'), {
        pageSetup: { orientation: 'landscape' }
    })
    ws.properties.defaultRowHeight = 20

    // 1) กำหนด column widths
    const cols: Partial<ExcelJS.Column>[] = [
        { header: 'ลำดับ', width: 6 },
        { header: 'วันที่', width: 12 },
        { header: 'เลขที่ใบเสร็จ', width: 20 },
        { header: 'รหัสผู้ป่วย', width: 14 },
        { header: 'รหัสผู้ป่วยในสาขา', width: 18 },
        ...(rpt.includeSn ? [{ header: 'SN', width: 12 }] : []),
        { header: 'ชื่อผู้ป่วย', width: 20 },
        { header: 'ประเภท Gift Voucher', width: 18 },
        { header: 'Gift Voucher', width: 30 },
        { header: 'รหัส', width: 12 },
        { header: 'มูลค่า', width: 12 },
        { header: 'ยอดลด (บาท)', width: 14 }
    ]
    ws.columns = cols as ExcelJS.Column[]

    let r = 1
    // 2) Title
    ws.mergeCells(r, 1, r, ws.columns.length)
    ws.getCell(r, 1).value = rpt.t('REPORT.TABLE.GIFT_VOUCHER_USED.TITLE')
    ws.getCell(r, 1).font = { size: 16, bold: true }
    ws.getCell(r, 1).alignment = { horizontal: 'center' }
    r++

    // 3) Filters
    ws.mergeCells(r, 1, r, ws.columns.length)
    ws.getCell(r, 1).value = [
        `${rpt.t('REPORT.FILTER.BRANCH')}: ${rpt.branchName}`,
        `${rpt.t('REPORT.FILTER.START')}:  ${rpt.dateStart}`,
        `${rpt.t('REPORT.FILTER.TO')}:   ${rpt.dateEnd}`,
        `${rpt.t('REPORT.FILTER.GIFT_VOUCHER_USED.TYPE')}: ${rpt.typeFilter}`,
        `${rpt.t('REPORT.FILTER.GIFT_VOUCHER_USED.PROMOTION')}: ${rpt.giftFilter}`
    ].join('    ')
    ws.getCell(r, 1).font = { size: 12 }
    ws.getCell(r, 1).alignment = { horizontal: 'left' }
    r += 2

    // 4) Header row
    const headerRow = ws.addRow(ws.columns.map((c: any) => (c.header as string)))
    headerRow.height = 24
    headerRow.font = { bold: true }
    headerRow.alignment = { horizontal: 'center', vertical: 'middle' }
    headerRow.eachCell((cell: any) => {
        cell.border = {
            top: { style: 'thin' }, left: { style: 'thin' },
            bottom: { style: 'thin' }, right: { style: 'thin' }
        }
    })
    r++

    if (!rpt.rows || rpt.rows.length === 0) {
        const noDataRow = ws.addRow([rpt.t('REPORT.TITLE.NO_DATA')])
        ws.mergeCells(noDataRow.number, 1, noDataRow.number, cols.length)
        noDataRow.font = { italic: true, color: { argb: 'FF000000' } }
        noDataRow.alignment = { horizontal: 'center' }
    }

    // 5) Data rows
    rpt.rows.forEach((d: any, i: number) => {
        const vals: any[] = [
            i + 1,
            d.date ? toBuddhistYear(moment(d.date), 'DD/MM/YYYY') : '-',
            d.receiptNumber,
            d.cnNumber,
            d.branchCnNumber
        ]
        if (rpt.includeSn) vals.push(d.snCode || '-')
        vals.push(
            d.patientName,
            d.voucherTypeName,
            d.voucherName,
            d.voucherCode,
            d.voucherPrice,
            d.voucherDiscount
        )
        const row = ws.addRow(vals)
        row.alignment = { vertical: 'middle' }
        row.eachCell((cell: any, col: any) => {
            // ขอบ cell
            cell.border = {
                top: { style: 'thin' }, left: { style: 'thin' },
                bottom: { style: 'thin' }, right: { style: 'thin' }
            }

            // คอลัมน์แรก (i+1) จัดกลางแนวนอน
            if (col === 1) {
                cell.alignment = { horizontal: 'center', vertical: 'middle' }
            }
            // คอลัมน์ “ยอดลด (บาท)” จัดขวา
            else if (col === ws.columns.length) {
                cell.alignment = { horizontal: 'right', vertical: 'middle' }
                if (typeof cell.value === 'number') cell.numFmt = '#,##0.00'
            }
            // คอลัมน์มูลค่า จัดขวา
            else if (col === ws.columns.length - 1) {
                cell.alignment = { horizontal: 'right', vertical: 'middle' }
            }
        })
    })

    // 6) ว่าง 1 แถว
    r = ws.lastRow.number + 1
    ws.mergeCells(r, 1, r, ws.columns.length)
    r++


    // 7) Summary table header (ใช้ columns ใหม่ไม่ต้อง header property)
    const sumCols = [
        { header: 'ประเภท Gift Voucher', width: 18 },
        { header: 'Gift Voucher', width: 30 },
        { header: 'มูลค่า', width: 12 },
        { header: 'จำนวน (ครั้ง)', width: 12 },
        { header: 'ยอดลด (บาท)', width: 14 }
    ]
    // สร้างคอลัมน์ summary ชั่วคราว
    sumCols.forEach((c, i) => ws.getColumn(i + 1).width = c.width)
    const hdrVals = sumCols.map(c => c.header)
    const sr = ws.addRow(hdrVals)
    sr.font = { bold: true }
    sr.alignment = { horizontal: 'center', vertical: 'middle' }
    sr.eachCell((cell: any) => {
        cell.border = {
            top: { style: 'thin' }, left: { style: 'thin' },
            bottom: { style: 'thin' }, right: { style: 'thin' }
        }
    })
    r++

    if (!rpt.groups || rpt.groups.length === 0) {
        const noDataRow = ws.addRow([rpt.t('REPORT.TITLE.NO_DATA')])
        ws.mergeCells(noDataRow.number, 1, noDataRow.number, sumCols.length)
        noDataRow.font = { italic: true, color: { argb: 'FF000000' } }
        noDataRow.alignment = { horizontal: 'center' }
        forceTextOnDateCells(wb)
        const buf = await wb.xlsx.writeBuffer()
        saveAs(
            new Blob([buf]),
            `${rpt.t('REPORT.TABLE.GIFT_VOUCHER_USED.TITLE')}_${moment().format('YYYYMMDD')}.xlsx`
        )
        return
    }

    // 8) Summary data grouped
    rpt.groups.forEach((g: any) => {
        const startRow = r
        g.vouchers.forEach((v: any, i: number) => {
            const vals = [
                g.voucherTypeName,
                v.voucherName,
                v.value,
                v.countUsed,
                v.sumDiscount
            ]
            const row = ws.addRow(vals)
            row.eachCell((cell: any, col: any) => {
                cell.border = {
                    top: { style: 'thin' }, left: { style: 'thin' },
                    bottom: { style: 'thin' }, right: { style: 'thin' }
                }
                // columns 4–5 จัดขวา
                if (col >= 4) cell.alignment = { horizontal: 'right', vertical: 'middle' }
                else cell.alignment = { horizontal: 'left', vertical: 'middle' }
            })
            r++
        })
        // merge ประเภท Gift Voucher
        ws.mergeCells(startRow, 1, r - 1, 1)
    })

    // 9) Summary total
    const totalCount = rpt.groups.reduce((s, g) => s + g.vouchers.reduce((a, v) => a + v.countUsed, 0), 0)
    const totalDiscount = rpt.groups.reduce((s, g) => s + g.vouchers.reduce((a, v) => a + v.sumDiscount, 0), 0)
    const footer = ['', '', '', totalCount, totalDiscount]
    const fr = ws.addRow(footer)
    fr.getCell(3).value = 'รวม'   // cell C
    fr.eachCell((cell: any, col: any) => {
        cell.font = { bold: true }
        cell.border = {
            top: { style: 'thin' }, left: { style: 'thin' },
            bottom: { style: 'thin' }, right: { style: 'thin' }
        }
        if (col >= 4) {
            cell.numFmt = '#,##0.00'
            cell.alignment = { horizontal: 'right', vertical: 'middle' }
        } else {
            cell.alignment = { horizontal: 'left', vertical: 'middle' }
        }
    })

    // 10) Save
    forceTextOnDateCells(wb)
    const buf = await wb.xlsx.writeBuffer()
    saveAs(
        new Blob([buf]),
        `${rpt.t('REPORT.TABLE.GIFT_VOUCHER_USED.TITLE')}_${moment().format('YYYYMMDD')}.xlsx`
    )
}
